import Swiper, { Autoplay, Navigation, Scrollbar } from 'swiper';
import {swiperObserver} from '../../utils/swiper.utils.js';

const initializedWrappers = new WeakSet();
const resizeVacanciesMediaQueries = new WeakMap();
const equalizersRegistry = new WeakMap();
const rowTopTolerance = 4;
const equalizedSelectors = [
    '.job-card__meta',
    '.job-card__title',
    '.job-card__details',
];

const getVacanciesSlides = (swiperEl) => {
    return Array.from(
        swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
    );
};

const getVacanciesSlideRows = (swiperEl) => {
    const slides = getVacanciesSlides(swiperEl);
    const swiper = swiperEl.swiper;

    if (!slides.length) {
        return [];
    }

    if (swiper && !swiper.destroyed) {
        return [slides];
    }

    const rows = [];

    slides.forEach((slide) => {
        const top = slide.getBoundingClientRect().top;
        const existingRow = rows.find((row) => {
            return Math.abs(row.top - top) <= rowTopTolerance;
        });

        if (existingRow) {
            existingRow.slides.push(slide);
            existingRow.top = Math.min(existingRow.top, top);
            return;
        }

        rows.push({
            top,
            slides: [slide],
        });
    });

    return rows
        .sort((a, b) => a.top - b.top)
        .map((row) => row.slides);
};

const getVacanciesEqualizerGroups = (swiperEl) => {
    const rows = getVacanciesSlideRows(swiperEl);
    const groups = [];

    rows.forEach((row) => {
        equalizedSelectors.forEach((selector) => {
            const elements = row
                .map((slide) => slide.querySelector(selector))
                .filter(Boolean);

            if (elements.length) {
                groups.push(elements);
            }
        });
    });

    return groups;
};

const setupVacanciesEqualizer = (swiperEl) => {
    const equalizer = equalizersRegistry.get(swiperEl);

    if (equalizer) {
        equalizer.refresh();
        return;
    }

    if (typeof window.lineTitlesFunc !== 'function') {
        return;
    }

    equalizersRegistry.set(
        swiperEl,
        window.lineTitlesFunc([], {
            getGroups: () => getVacanciesEqualizerGroups(swiperEl),
        })
    );
};

const getVacanciesWrappers = (scope = document) => {
    const root = scope instanceof Element ? scope : document;

    if (root.matches?.('.vacancies-swiper-wr')) {
        return [root];
    }

    return Array.from(root.querySelectorAll('.vacancies-swiper-wr'));
};

const destroySwiper = (swiperEl) => {
    const swiper = swiperEl?.swiper;

    if (swiper && !swiper.destroyed) {
        swiper.destroy(true, true);
    }
};

const getVacanciesControls = (wrapper) => {
    const scrollbarEl = wrapper.querySelector('.swiper-scrollbar');
    const nextEl = wrapper.querySelector('.swiper-button-next');
    const prevEl = wrapper.querySelector('.swiper-button-prev');

    return {
        scrollbarEl,
        navigation: nextEl && prevEl ? {nextEl, prevEl} : undefined,
    };
};

const createVacanciesSwiper = (swiperEl, wrapper) => {
    const {scrollbarEl, navigation} = getVacanciesControls(wrapper);
    const swiper = new Swiper(swiperEl, {
        modules: [Autoplay, Navigation, Scrollbar],
        observer: true,
        observeParents: true,
        spaceBetween: 19,
        slidesPerView: 1,
        threshold: 10,
        scrollbar: scrollbarEl ? {
            el: scrollbarEl,
            draggable: true,
            dragSize: 'auto',
        } : undefined,
        navigation,
        breakpoints: {
            552: {
                spaceBetween: 20,
                slidesPerView: 2,
            },
            740: {
                spaceBetween: 26,
                slidesPerView: 3,
            },
            1260: {
                spaceBetween: 41,
                slidesPerView: 3,
            },
        },
        autoplay: {
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: window.innerWidth >= 1033,
        },
    });

    swiperObserver(swiper);
};

const createResizeVacanciesSwiper = (swiperEl, wrapper) => {
    const {scrollbarEl, navigation} = getVacanciesControls(wrapper);
    const swiper = new Swiper(swiperEl, {
        modules: [Autoplay, Navigation, Scrollbar],
        observer: true,
        observeParents: true,
        slidesPerView: 1,
        spaceBetween: 19,
        autoplay: {
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
        },
        breakpoints: {
            552: {
                spaceBetween: 20,
                slidesPerView: 2,
            },
            740: {
                spaceBetween: 26,
                slidesPerView: 2,
            },
            951: {
                spaceBetween: 16,
                slidesPerView: 3,
            },
            1260: {
                spaceBetween: 41,
                slidesPerView: 3,
            },
        },
        scrollbar: scrollbarEl ? {
            el: scrollbarEl,
            draggable: true,
            dragSize: 'auto',
        } : undefined,
        navigation,
    });

    swiperObserver(swiper);
};

const syncResizeVacanciesSwiper = (wrapper) => {
    const swiperEl = wrapper.querySelector('.swiper');

    if (!swiperEl) {
        return;
    }

    const slidesCount = getVacanciesSlides(swiperEl).length;
    const mediaQuery = resizeVacanciesMediaQueries.get(wrapper)?.mediaQuery
        ?? window.matchMedia('(max-width: 1023px)');

    if (slidesCount <= 3) {
        destroySwiper(swiperEl);
        swiperEl.classList.add('no-swiper-type');
        setupVacanciesEqualizer(swiperEl);
        return;
    }

    if (!mediaQuery.matches) {
        destroySwiper(swiperEl);
        swiperEl.classList.remove('no-swiper-type');
        setupVacanciesEqualizer(swiperEl);
        return;
    }

    swiperEl.classList.remove('no-swiper-type');

    if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
        swiperEl.swiper.update();
    } else {
        createResizeVacanciesSwiper(swiperEl, wrapper);
    }

    setupVacanciesEqualizer(swiperEl);
};

const bindResizeVacanciesSwiper = (wrapper) => {
    if (resizeVacanciesMediaQueries.has(wrapper)) {
        syncResizeVacanciesSwiper(wrapper);
        return;
    }

    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const listener = () => syncResizeVacanciesSwiper(wrapper);

    resizeVacanciesMediaQueries.set(wrapper, {mediaQuery, listener});
    mediaQuery.addEventListener('change', listener);
    syncResizeVacanciesSwiper(wrapper);
};

const destroyVacanciesSwiper = (scope = document) => {
    getVacanciesWrappers(scope).forEach((wrapper) => {
        destroySwiper(wrapper.querySelector('.swiper'));
    });
};

const refreshVacanciesSwiper = (scope = document) => {
    getVacanciesWrappers(scope).forEach((wrapper) => {
        const swiperEl = wrapper.querySelector('.swiper');

        if (!swiperEl) {
            return;
        }

        if (swiperEl.classList.contains('on-resize-type')) {
            bindResizeVacanciesSwiper(wrapper);
            return;
        }

        const slidesCount = getVacanciesSlides(swiperEl).length;

        if (slidesCount <= 3) {
            destroySwiper(swiperEl);
            swiperEl.classList.add('no-swiper-type');
        } else {
            swiperEl.classList.remove('no-swiper-type');

            if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
                swiperEl.swiper.update();
            } else {
                createVacanciesSwiper(swiperEl, wrapper);
            }
        }

        setupVacanciesEqualizer(swiperEl);
    });
};

const initVacanciesSwiper = (scope = document) => {
    getVacanciesWrappers(scope).forEach((wrapper) => {
        if (initializedWrappers.has(wrapper)) {
            refreshVacanciesSwiper(wrapper);
            return;
        }

        initializedWrappers.add(wrapper);
        refreshVacanciesSwiper(wrapper);
    });
};

initVacanciesSwiper();
window.initVacanciesSwiper = initVacanciesSwiper;
window.destroyVacanciesSwiper = destroyVacanciesSwiper;
window.refreshVacanciesSwiper = refreshVacanciesSwiper;
